

import { useState } from "react";
import type { Appointment, AppointmentStatus } from "@/lib/slotting/types";
import { APPOINTMENT_STATUS_COLORS } from "@/lib/slotting/statusColors";
import { IconTrash } from "@tabler/icons-react";
import { useGuestAppointments } from "@/providers/GuestAppointments";
import { useCapabilities } from "@/providers/CapabilitiesProvider";
import { useTranslations } from "next-intl";
import { useUserLocale } from "@/hooks/useUserLocale";
import { useEventTimezone } from "@/providers/EventProvider";
import { PDeleteConfirmation } from "@/components/PDeleteConfirmation/PDeleteConfirmation";
import { AnimatePresence } from "framer-motion";

interface AppointmentEntryProps {
  appointment: Appointment;
  firstName?: string;
  lastName?: string;
  onPreview: (appointment: Appointment) => void;
}

const STATUS_TRANSLATION_KEY: Record<AppointmentStatus, string> = {
  TENTATIVE: "statusTentative",
  CONFIRMED: "statusConfirmed",
  CHECKED_IN: "statusCheckedIn",
  CANCELLED: "statusCancelled",
};

// startDt/endDt are real UTC instants — render in the event tz, not browser tz.
function formatDate(dt: string, locale: string, tz: string): string {
  try {
    const d = new Date(dt);
    return d.toLocaleDateString(locale, {
      timeZone: tz,
      day: "numeric",
      month: "short",
    });
  } catch {
    return dt;
  }
}

function formatTimeRange(
  startDt: string,
  endDt: string,
  locale: string,
  tz: string,
): string {
  try {
    const s = new Date(startDt);
    const e = new Date(endDt);
    const fmt = (d: Date) =>
      d.toLocaleTimeString(locale, {
        timeZone: tz,
        hour: "2-digit",
        minute: "2-digit",
      });
    return `${fmt(s)} – ${fmt(e)}`;
  } catch {
    return "";
  }
}

export const AppointmentEntry: React.FC<AppointmentEntryProps> = ({
  appointment,
  onPreview,
}) => {
  const ts = useTranslations("slotting");
  const tg = useTranslations("guestDetails");
  const locale = useUserLocale();
  const tz = useEventTimezone();
  const { deleteAppointment } = useGuestAppointments();
  // Deleting an appointment is a slotting mutation (backend requires
  // slotting:delete). Hide for roles that lack it — e.g. a read-only Auditor in
  // "preview as role" sees the appointment but no delete control.
  const { can } = useCapabilities();
  const canDeleteAppointment = can("slotting:delete");
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const colors =
    APPOINTMENT_STATUS_COLORS[appointment.status] ?? APPOINTMENT_STATUS_COLORS.TENTATIVE;
  const statusKey =
    STATUS_TRANSLATION_KEY[appointment.status] ?? "statusTentative";

  const handleConfirmDelete = async () => {
    setDeleting(true);
    await deleteAppointment(appointment.appointmentId);
    setDeleting(false);
    setShowConfirm(false);
  };

  return (
    <>
      <li
        onClick={() => onPreview(appointment)}
        className="rounded-xl px-3 py-2.5 border flex flex-col gap-1 outline-tertiary cursor-pointer hover:surface-secondary transition-colors"
      >
        <div className="flex flex-row items-start justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <span
              className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${colors.chipBg} ${colors.chipFg}`}
            >
              {ts(statusKey)}
            </span>
            <p className="body-3-medium truncate font-tertiary">
              {formatDate(appointment.startDt, locale, tz)}
            </p>
          </div>
          {canDeleteAppointment && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
              className="shrink-0"
            >
              <IconTrash size={14} className="font-error" />
            </button>
          )}
        </div>
        <p className="body-4-regular font-tertiary">
          {formatTimeRange(appointment.startDt, appointment.endDt, locale, tz)}
        </p>
      </li>
      <AnimatePresence>
        {showConfirm && (
          <PDeleteConfirmation
            title={tg("deleteConfirmationAppointment")}
            message={tg("deleteConfirmationAppointmentMessage")}
            onCloseAction={() => setShowConfirm(false)}
            onConfirmAction={handleConfirmDelete}
            isLoading={deleting}
          />
        )}
      </AnimatePresence>
    </>
  );
};
